// S5b.2: into the assessment from the client's journey (PRD §8.8, §10.5).
//
// The client's portal mints a single-use, two-minute ticket and opens
// /launch?t={ticket} here. This module spends it with the CRM's
// `redeemAssessmentTicket` (same Firebase project) and signs the client in as
// their own portal identity on an Auth instance of its OWN: a second named app
// with session persistence from the start.
//
// Never the default instance. Switching persistence on an instance that holds
// a user moves that user out of local storage and signs out every other tab of
// this site (the V9 spike, apc-crm `scripts/spikes/v9-session-persistence`,
// showed both halves). The launch identity lives only in this tab.
//
// What a launch keeps for the rest of the tab is `{grantId, clientId, coachId,
// attempt, assessmentKey, returnTo}` in session storage. How results save is
// unchanged here; S5b.3 reads this.
import { initializeApp } from 'firebase/app';
import { initializeAuth, browserSessionPersistence, signInWithCustomToken, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { app } from './firebase';
import { PORTAL_HOME } from './config';

export const LAUNCH_STATE_KEY = 'apcLaunch';

/** Each assessment's existing start. */
export const LAUNCH_ROUTES = {
  workstyle: '/work-style',
  skills: '/motivated-skills',
  constellation: '/cosmic-registration',
};

/** Where a client goes when there is no launch to return them from. D.1: it lives in config.js. */
export { PORTAL_HOME };

let launchApp = null;
let launchAuth = null;

export function getLaunchAuth() {
  if (launchAuth) return launchAuth;
  launchApp = initializeApp(app.options, 'launch');
  launchAuth = initializeAuth(launchApp, { persistence: browserSessionPersistence });
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    connectAuthEmulator(launchAuth, 'http://localhost:9099', { disableWarnings: true });
  }
  return launchAuth;
}

let launchFns = null;
let launchDb = null;

/** The launch app's Functions, connected once. S5b.3: the identity module hands it to the saves. */
export function launchFunctions() {
  if (launchFns) return launchFns;
  getLaunchAuth();
  launchFns = getFunctions(launchApp);
  if (process.env.REACT_APP_USE_EMULATORS === 'true') connectFunctionsEmulator(launchFns, 'localhost', 5001);
  return launchFns;
}

/** The launch app's Firestore, so a launched save is written as the launch user. */
export function launchFirestore() {
  if (launchDb) return launchDb;
  getLaunchAuth();
  launchDb = getFirestore(launchApp);
  if (process.env.REACT_APP_USE_EMULATORS === 'true') connectFirestoreEmulator(launchDb, 'localhost', 8080);
  return launchDb;
}

/** True when this tab was opened by a launch (its state is kept), whether or not its user is still signed in. */
export function hasLaunchState() {
  return !!readLaunchState();
}

/** Remove `t` from the address bar and history, and hand it back. */
export function takeTicketFromUrl(win = window) {
  const url = new URL(win.location.href);
  const ticket = url.searchParams.get('t');
  url.searchParams.delete('t');
  win.history.replaceState(win.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return ticket;
}

export function readLaunchState() {
  try {
    const raw = window.sessionStorage.getItem(LAUNCH_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function journeyLink(state = readLaunchState()) {
  return state && typeof state.returnTo === 'string' ? state.returnTo : PORTAL_HOME;
}

/** Spend the ticket, sign in on the launch instance, keep the launch, answer where to go. */
export async function redeemAndSignIn(ticket) {
  const redeem = httpsCallable(launchFunctions(), 'redeemAssessmentTicket');
  const { data } = await redeem({ ticket });
  await signInWithCustomToken(getLaunchAuth(), data.token);
  const state = {
    grantId: data.grantId,
    clientId: data.clientId,
    coachId: data.coachId,
    attempt: data.attempt,
    assessmentKey: data.assessmentKey,
    returnTo: data.returnTo,
    // S5b.3: a Constellation grant carries the Complete Universe (read from the grant by the server).
    premium: data.premium === true,
    // 2026-10-30 (Dan): a known client is not asked who they are; registration says who sees the results.
    clientFirstName: data.clientFirstName || null,
    clientCurrentRole: data.clientCurrentRole || null,
    coachName: data.coachName || null,
  };
  window.sessionStorage.setItem(LAUNCH_STATE_KEY, JSON.stringify(state));
  return { state, route: LAUNCH_ROUTES[data.assessmentKey] || null };
}

/** A full page load to a launched flow, so the auth provider mounts with the launch already held. */
export function fullLoad(route) {
  window.location.replace(route);
}
